import { Routes, Route } from 'react-router-dom'
import Layout from './components/Layout'
import HomePage from './pages/HomePage'
import NotFoundPage from './pages/NotFoundPage'
import AuthSocialCompletePage from './pages/AuthSocialCompletePage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import AdminFlagsPage from './pages/AdminFlagsPage'

// =============================================================================
// CODERBLOCK NAVIGATION TRACKING
// Auto-initialize navigation tracking for Coderblock editor integration.
// (Patches the History API + relays the current path to the editor iframe.)
// =============================================================================
if (typeof window !== 'undefined' && window.parent !== window) {
  const sendNavigationUpdate = () => {
    const path = window.location.pathname + window.location.search + window.location.hash
    window.parent.postMessage({
      type: 'coderblock-navigation',
      mode: 'live',
      path: path || '/'
    }, '*')
  }

  const originalPushState = history.pushState
  const originalReplaceState = history.replaceState

  history.pushState = function(...args: Parameters<typeof history.pushState>) {
    originalPushState.apply(history, args)
    setTimeout(sendNavigationUpdate, 0)
  }

  history.replaceState = function(...args: Parameters<typeof history.replaceState>) {
    originalReplaceState.apply(history, args)
    setTimeout(sendNavigationUpdate, 0)
  }

  window.addEventListener('popstate', sendNavigationUpdate)
  sendNavigationUpdate()

  // Listen for navigation commands from Coderblock editor (avoids iframe reload flash)
  window.addEventListener('message', (event: MessageEvent) => {
    if (event.data?.type === 'coderblock-navigate' && event.data.path) {
      const target = event.data.path
      if (window.location.pathname !== target) {
        history.pushState(null, '', target)
        window.dispatchEvent(new PopStateEvent('popstate'))
      }
    }
  })
}

// =============================================================================
// 🧭 EXPLICIT ROUTES
// =============================================================================
// Every page is registered here BY HAND. To add a page:
//   1. Create the component file in src/pages/ (a real import, not an example)
//   2. Import it above, then add a <Route> inside the <Layout> route below:
//        <Route path="about" element={<YourNewPage />} />   // → /about
//
// Notes:
//   - `index`     → the "/" home route
//   - `path="*"`  → the 404 catch-all (keep it LAST)
//   - All routes sit inside the <Layout> route, so they share the Layout
//     shell (header/footer slots, analytics, page-level error boundary).
//   - Dynamic params: <Route path="products/:id" element={<ProductDetailPage />} />
// =============================================================================

export default function AppRouter() {
  return (
    <Routes>
      <Route path="/" element={<Layout />}>
        <Route index element={<HomePage />} />
        {/* OAuth social-login callback — do NOT link to it (relay return target) */}
        <Route path="auth/social/complete" element={<AuthSocialCompletePage />} />
        {/* Password reset landing — target of the email link (do NOT link to it) */}
        <Route path="reset-password" element={<ResetPasswordPage />} />
        {/* Admin flags review — password-gated (MVP) */}
        <Route path="admin" element={<AdminFlagsPage />} />
        {/* Shared roads and routes — the map, deep-linked to a selection */}
        <Route path="road/:publicId" element={<HomePage />} />
        <Route path="route/:publicId" element={<HomePage />} />
        {/* Add new page routes here */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  )
}
